/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Chart } from 'react-google-charts';

import {
  addMonths,
  eachMonthOfInterval,
  endOfMonth,
  format,
  parseISO,
} from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import {
  formatPrice,
  formatNumber2CasasDecimais,
  TIPO_USUARIO,
} from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { ContainerFlexWrap, RelatorioHeader } from './styles';
import getResponseErrors from '../../../utils/getResponseErrors';
import { Colors } from '../../../styles/constants';
import HeaderSimples from '../components/HeaderSimples';

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IGenericType {
  [key: string]: any;
}

interface IGraficos {
  clinica: string;
  graficoTotal: IGenericType[];
  graficoConsulta: IGenericType[];
  graficoExame: IGenericType[];
  graficoOdontologia: IGenericType[];
  dadosDespesa: IGenericType[];
}

export interface IItemResumoMes {
  tipo: string;
  descricao: string;
  mes: string;
  total: number;
}

interface IRetornoAgendamentos {
  CliCodigo: number;
  CliNome: string;
  valores: IItemResumoMes[];
}

const DashboardAgendamento: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  // const formRefBusca = useRef<FormHandles>(null);
  const formRefBuscaAcumulado = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  // const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio>();
  const [graficoAgendamentos, setgraficoAgendamentos] = useState<
    IGenericType[]
  >();
  const [
    graficoAgendamentosAcumulado,
    setGraficoAgendamentosAcumulado,
  ] = useState<IGraficos[]>();
  const graficoColors = [Colors.primary, Colors.verde, Colors.danger];
  // const [totais, setTotais] = useState<ITotais>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const initialDataAcumulado = {
    dataInicio: format(addMonths(new Date(), -6), 'yyyy-MM'),
    dataFim: format(new Date(), 'yyyy-MM'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [clinica, setClinica] = useState('');

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const handleSubmitBuscaAcumulado = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBuscaAcumulado.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = new Date(
          `${dados.dataInicio.toString()}-01 04:00:00`,
        );
        const dataFimSelecionada = endOfMonth(
          new Date(`${dados.dataFim.toString()}-01 04:00:00`),
        );

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const parametrosOpcionais = dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';

        const response = await api.get<IRetornoAgendamentos[]>(
          `/relatorios/dashboardagendamento?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        const graficoLegenda: IGenericType[] = [
          ['Mes', 'Agendado', 'Atendido', 'cancelado'],
        ];

        const graficoGeral: IGraficos[] = [];

        const meses = eachMonthOfInterval({
          start: dataInicioSelecionada,
          end: dataFimSelecionada,
        }).map(data => format(data, 'MM/yyyy'));
        response.data.forEach(rel => {
          const consultas: IGenericType[] = [];
          const exames: IGenericType[] = [];
          const odontologias: IGenericType[] = [];
          const totais: IGenericType[] = [];
          meses.forEach(mes => {
            const mesFiltrado = rel.valores.filter(val => val.mes === mes);

            const consulta = mesFiltrado.filter(val => val.tipo === 'consulta');
            const consultaAgendado =
              consulta.find(val => val.descricao === 'agendado')?.total || 0;
            const consultaAtendido =
              consulta.find(val => val.descricao === 'atendido')?.total || 0;
            const consultaCancelado =
              consulta.find(val => val.descricao === 'cancelado')?.total || 0;

            const exame = mesFiltrado.filter(val => val.tipo === 'exame');
            const exameAgendado =
              exame.find(val => val.descricao === 'agendado')?.total || 0;
            const exameAtendido =
              exame.find(val => val.descricao === 'atendido')?.total || 0;
            const exameCancelado =
              exame.find(val => val.descricao === 'cancelado')?.total || 0;

            const odontologia = mesFiltrado.filter(
              val => val.tipo === 'odontologia',
            );
            const odontologiaAgendado =
              odontologia.find(val => val.descricao === 'agendado')?.total || 0;
            const odontologiaAtendido =
              odontologia.find(val => val.descricao === 'atendido')?.total || 0;
            const odontologiaCancelado =
              odontologia.find(val => val.descricao === 'cancelado')?.total ||
              0;

            const totalAgendado =
              consultaAgendado + exameAgendado + odontologiaAgendado;
            const totalAtendido =
              consultaAtendido + exameAtendido + odontologiaAtendido;
            const totalCancelado =
              consultaCancelado + exameCancelado + odontologiaCancelado;

            consultas.push([
              mes,
              consultaAgendado,
              consultaAtendido,
              consultaCancelado,
            ]);
            exames.push([mes, exameAgendado, exameAtendido, exameCancelado]);
            odontologias.push([
              mes,
              odontologiaAgendado,
              odontologiaAtendido,
              odontologiaCancelado,
            ]);
            totais.push([mes, totalAgendado, totalAtendido, totalCancelado]);
          });
          graficoGeral.push({
            clinica: rel.CliNome,
            graficoTotal: graficoLegenda.concat(totais),
            graficoConsulta: graficoLegenda.concat(consultas),
            graficoExame: graficoLegenda.concat(exames),
            graficoOdontologia: graficoLegenda.concat(odontologias),
            dadosDespesa: [],
          });
        });

        setGraficoAgendamentosAcumulado(graficoGeral);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBuscaAcumulado.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    formRefBuscaAcumulado.current?.submitForm();
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="bg-white border p-2 my-3 d-flex justify-content-between align-items-center">
          <HeaderSimples />
          <h2 id="topoBusca">Dashboard de Agendamento</h2>

          <span className="pr-2">Impresso em {dataExibe?.dataHora}</span>
        </div>

        <div className="d-print-none bg-white border p-2 mb-2">
          <RelatorioHeader className="mt-2">
            AGENDAMENTOS POR TIPO {dataExibe?.dataDiaSemana}
          </RelatorioHeader>
          <div className="bg-white border p-2">
            <div className="d-print-none">
              {optionsClinica.length > 0 && user && (
                <Form
                  ref={formRefBuscaAcumulado}
                  initialData={initialDataAcumulado}
                  onSubmit={handleSubmitBuscaAcumulado}
                >
                  <Row>
                    <Col md={2}>
                      <Input
                        name="dataInicio"
                        label="De"
                        type="month"
                        required
                      />
                    </Col>
                    <Col md={2}>
                      <Input name="dataFim" label="Até" type="month" required />
                    </Col>
                    <Col md={2}>
                      <Select
                        name="CliCodigo"
                        label="Clinica"
                        options={optionsClinica}
                        disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                      />
                    </Col>
                    <Col md={2}>
                      <br />
                      <Button
                        type="submit"
                        size="sm"
                        variant="outline-secondary"
                      >
                        BUSCAR
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </div>

            {graficoAgendamentosAcumulado &&
              graficoAgendamentosAcumulado.map(cli => (
                <ContainerFlexWrap key={`principal${cli.clinica}`}>
                  <div>
                    <div>
                      <Chart
                        key={`grafTotal-${cli.clinica}`}
                        chartType="ColumnChart"
                        data={cli.graficoTotal}
                        options={{
                          title: `Total ${cli.clinica}`,
                          is3D: true,
                          legend: { position: 'bottom' },
                          colors: graficoColors,
                        }}
                        width="100%"
                        height="300px"
                        legendToggle
                      />
                    </div>

                    <div>
                      <Chart
                        key={`grafConsulta-${cli.clinica}`}
                        chartType="ColumnChart"
                        data={cli.graficoConsulta}
                        options={{
                          title: `Consultas ${cli.clinica}`,
                          legend: { position: 'bottom' },
                          colors: graficoColors,
                        }}
                        width="100%"
                        height="300px"
                        legendToggle
                      />
                    </div>
                  </div>

                  <div>
                    <div>
                      <Chart
                        key={`grafExame-${cli.clinica}`}
                        chartType="ColumnChart"
                        data={cli.graficoExame}
                        options={{
                          title: `Exames ${cli.clinica}`,
                          legend: { position: 'bottom' },
                          colors: graficoColors,
                        }}
                        width="100%"
                        height="300px"
                        legendToggle
                      />
                    </div>

                    <div>
                      <Chart
                        key={`grafOdontologia-${cli.clinica}`}
                        chartType="ColumnChart"
                        data={cli.graficoOdontologia}
                        options={{
                          title: `Odontologia ${cli.clinica}`,
                          legend: { position: 'bottom' },
                          colors: graficoColors,
                        }}
                        width="100%"
                        height="300px"
                        legendToggle
                      />
                    </div>
                  </div>
                </ContainerFlexWrap>
              ))}
          </div>
        </div>
      </>
    </Container>
  );
};

export default DashboardAgendamento;
